"use client";

import { createContext, ReactNode, useContext } from "react";

import { HelpDeskSecurityContextType } from "@/types/helpDesk";
import {
  AnswerValidityResponse,
  QuestionRecord,
} from "@/types/securitySettings";

import { useAuth } from "@/context/util/auth";
import { useUi } from "@/context/util/ui";

import { getErrorMessage } from "@/lib/data";
import { httpRequest } from "@/lib/http";
import { fetchAnswerValidity } from "@/lib/securitySettings";

const SecurityContext = createContext<HelpDeskSecurityContextType>(null!);

export function SecurityProvider({ children }: { children: ReactNode }) {
  const { setLoading, setSnackbar } = useUi();
  const { getAccessToken } = useAuth();

  async function isAnswerValid(
    securityQuestion: QuestionRecord,
    source: string = "Account Utilities",
    username: string
  ) {
    try {
      const token = await getAccessToken();

      if (!token) throw new Error("No access token available");

      const body = JSON.stringify({ securityQuestion, source, username });
      const headers = { Authorization: `Bearer ${token}` };

      const answerValidityResponse = (await fetchAnswerValidity(
        headers,
        body
      )) as AnswerValidityResponse;

      if (!answerValidityResponse) throw new Error("Empty http response");

      if (answerValidityResponse.isBlocked)
        throw new Error(
          "User has been temporarily blocked from answering security question"
        );

      const message = "Security answer verified";

      setSnackbar((prev) => ({
        ...prev,
        message,
        open: true,
        severity: "success",
      }));

      return answerValidityResponse;
    } catch (error) {
      const message =
        "Failed to verify user security answer. Please try again later.";
      console.error(message, error);

      setSnackbar((prev) => ({
        ...prev,
        message: getErrorMessage(error),
        open: true,
        severity: "error",
      }));
    }
  }

  async function resetPin(username: string) {
    try {
      setLoading(true);

      const token = await getAccessToken();

      if (!token) throw new Error("No access token available");

      const url = `/account-utilities/api/help-desk/security/reset/pin?username=${username}`;

      const headers = { Authorization: `Bearer ${token}` };
      const method = "PUT";

      const requestInit: RequestInit = { headers, method };

      const response = (
        await httpRequest<{ message: string }>(url, requestInit)
      ).data;

      if (!response) throw new Error("Empty http response");

      setSnackbar((prev) => ({
        ...prev,
        message: response.message,
        open: true,
        severity: "success",
      }));

      return response;
    } catch (error) {
      const message = "Failed to reset user pin. Please try again later.";
      console.error(message, error);

      setSnackbar((prev) => ({
        ...prev,
        message,
        open: true,
        severity: "error",
      }));
    } finally {
      setLoading(false);
    }
  }

  async function resetSelfService(username: string) {
    try {
      setLoading(true);

      const token = await getAccessToken();

      if (!token) throw new Error("No access token available");

      const url = `/account-utilities/api/help-desk/security/reset/self-service?username=${username}`;

      const headers = { Authorization: `Bearer ${token}` };
      const method = "PUT";

      const requestInit: RequestInit = { headers, method };

      const response = (
        await httpRequest<{ message: string }>(url, requestInit)
      ).data;

      if (!response) throw new Error("Empty http response");

      setSnackbar((prev) => ({
        ...prev,
        message: response.message,
        open: true,
        severity: "success",
      }));

      return response;
    } catch (error) {
      const message =
        "Failed to reset user self-service. Please try again later.";
      console.error(message, error);

      setSnackbar((prev) => ({
        ...prev,
        message,
        open: true,
        severity: "error",
      }));
    } finally {
      setLoading(false);
    }
  }

  return (
    <SecurityContext.Provider
      value={{ isAnswerValid, resetPin, resetSelfService }}
    >
      {children}
    </SecurityContext.Provider>
  );
}

export function useSecurityApi() {
  const ctx = useContext(SecurityContext);
  if (!ctx)
    throw new Error("useSecurityApi must be used within a SecurityContext");
  return ctx;
}
